import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Gauge, LogOut, FileText, Download, User, Building2, Calendar, Hash } from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { ClientSGMTab } from "@/components/client/ClientSGMTab";
import ClientSGMGroups from "@/components/client/ClientSGMGroups";

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInvoices = async () => {
    try {
      const { data } = await api.get("/invoices");
      setInvoices(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []);

  const handleDownload = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", inv.original_filename || `factura-${inv.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
  };
  const formatMoney = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
  const formatSize = (b) => (b ? `${(b / 1024).toFixed(0)} KB` : "—");

  return (
    <div className="min-h-screen bg-[#1A1A1F] text-white" data-testid="client-portal">
      {/* Top bar */}
      <header className="border-b border-[#2E2E33] bg-[#1F1F25] sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="portal-logo">
            <div className="w-9 h-9 sm:w-10 sm:h-10 border border-[#F59E0B] flex items-center justify-center bg-[#F59E0B]/10 flex-shrink-0">
              <Gauge className="w-5 h-5 text-[#F59E0B]" strokeWidth={2.5} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-heading font-black text-sm uppercase truncate">SGM México</div>
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-widest uppercase">Portal de Clientes</div>
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <div className="hidden md:flex items-center gap-2 text-sm">
              <div className="w-8 h-8 bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex items-center justify-center">
                <User className="w-4 h-4 text-[#F59E0B]" />
              </div>
              <div className="leading-tight">
                <div className="text-white text-sm font-semibold">{user?.name}</div>
                <div className="text-[#71717A] text-xs font-mono">{user?.email}</div>
              </div>
            </div>
            <ThemeToggle testId="portal-theme-toggle" />
            <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost px-3 py-2 sm:px-7 sm:py-4" data-testid="portal-logout">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-12">
        <div className="mb-8 sm:mb-12">
          <div className="overline mb-3">// Bienvenido</div>
          <h1 className="font-heading font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
            Hola, <span className="text-[#F59E0B]">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-[#A1A1AA] mt-3 text-sm sm:text-base">Aquí encontrará todas las facturas emitidas para su empresa.</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#2E2E33] border border-[#2E2E33] mb-8 sm:mb-12">
          <div className="bg-[#1F1F25] p-4 sm:p-6 min-w-0">
            <div className="font-mono text-[10px] sm:text-xs text-[#71717A] uppercase tracking-widest mb-1 sm:mb-2">// Total Facturas</div>
            <div className="font-heading font-black text-3xl sm:text-4xl text-white">{invoices.length}</div>
          </div>
          <div className="bg-[#1F1F25] p-4 sm:p-6 min-w-0">
            <div className="font-mono text-[10px] sm:text-xs text-[#71717A] uppercase tracking-widest mb-1 sm:mb-2">// Monto Total</div>
            <div className="font-heading font-black text-2xl sm:text-4xl text-[#F59E0B] truncate">
              {formatMoney(invoices.reduce((s, i) => s + (i.amount || 0), 0))}
            </div>
          </div>
          <div className="bg-[#1F1F25] p-6">
            <div className="font-mono text-xs text-[#71717A] uppercase tracking-widest mb-2">// Empresa</div>
            <div className="font-heading font-bold text-lg text-white flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#F59E0B]" />
              {user?.company || "—"}
            </div>
          </div>
        </div>

        {/* Invoices */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h2 className="font-heading font-bold uppercase text-xl sm:text-2xl tracking-tight">
            <FileText className="inline w-6 h-6 text-[#F59E0B] mr-2 -mt-1" />
            Mis Facturas
          </h2>
          <span className="font-mono text-[10px] sm:text-xs text-[#71717A] uppercase tracking-widest">[ {invoices.length} REGISTROS ]</span>
        </div>

        <div className="border border-[#2E2E33] bg-[#1F1F25]" data-testid="invoices-table">
          {loading ? (
            <div className="p-12 text-center text-[#A1A1AA] font-mono text-sm">Cargando facturas...</div>
          ) : invoices.length === 0 ? (
            <div className="p-16 text-center">
              <FileText className="w-12 h-12 text-[#2E2E33] mx-auto mb-4" />
              <p className="text-[#A1A1AA] mb-2">Aún no tiene facturas asignadas.</p>
              <p className="text-[#71717A] text-sm font-mono uppercase tracking-widest">
                // Su administrador subirá los documentos pronto
              </p>
            </div>
          ) : (
            <>
              {/* Header row */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
                <div className="col-span-2">Folio</div>
                <div className="col-span-3">Descripción</div>
                <div className="col-span-2">Periodo</div>
                <div className="col-span-2">Fecha</div>
                <div className="col-span-2 text-right">Monto</div>
                <div className="col-span-1 text-right">Acción</div>
              </div>
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 border-b border-[#2E2E33] last:border-b-0 hover:bg-[#26262C] transition-colors items-center"
                  data-testid={`invoice-row-${inv.id}`}
                >
                  <div className="md:col-span-2 flex items-center gap-2">
                    <Hash className="w-3.5 h-3.5 text-[#F59E0B]" />
                    <span className="font-mono text-sm text-white">{inv.invoice_number || inv.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="md:col-span-3 text-sm text-[#D4D4D8]">{inv.description || "Factura de servicios SGM"}</div>
                  <div className="md:col-span-2 text-sm text-[#A1A1AA] font-mono">{inv.period || "—"}</div>
                  <div className="md:col-span-2 text-sm text-[#A1A1AA] flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" /> {formatDate(inv.uploaded_at)}
                  </div>
                  <div className="md:col-span-2 text-right">
                    <div className="font-heading font-bold text-[#F59E0B]">{formatMoney(inv.amount)}</div>
                    <div className="font-mono text-[10px] text-[#71717A]">{formatSize(inv.size)}</div>
                  </div>
                  <div className="md:col-span-1 flex md:justify-end">
                    <button
                      onClick={() => handleDownload(inv)}
                      className="inline-flex items-center gap-1.5 border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] px-3 py-2 transition-colors font-mono text-[10px] uppercase tracking-widest"
                      data-testid={`download-invoice-${inv.id}`}
                    >
                      <Download className="w-3.5 h-3.5" /> PDF
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <ClientSGMTab />
        <ClientSGMGroups />
      </main>
    </div>
  );
}
